// App settings: language, units, library start screen, theme, labels, calendar, holidays and the
// setting the Swift app stored but never used (layout) — kept as agreed, marked as unused.
import React from 'react';
import { Linking, Platform, Pressable, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { LANGUAGES, THEMES, useSettings } from '../store/settings';
import { CALENDARS } from '../logic/calendars';
import { formatDay } from '../hooks';
import { localDay } from '../logic/dates';
import { TRADITIONS } from '../logic/holidays';
import { Caption, Chip, Choice, ListItem, Row, Screen, Section, ToggleRow } from '../components/ui';
import { useNav } from '../navigation';
import DataFileButtons from '../components/DataFileButtons';
import type { UnitSystem } from '../logic/units';
import { useLabels } from '../labels';
import { radius, spacing, THEMES_DEF, usePalette } from '../theme';

const LANGUAGE_NAMES: Record<string, string> = { 'en-US': 'English', 'de-DE': 'Deutsch (Deutschland)', 'de-AT': 'Deutsch (Österreich)', 'de-CH': 'Deutsch (Schweiz)' };

export default function AppSettingsScreen() {
  const { t } = useTranslation();
  const s = useSettings();
  const nav = useNav();
  const p = usePalette();
  const labels = useLabels();
  const developer = s.theme === 'developer';
  const systems: UnitSystem[] = developer ? ['metric', 'usCustomary', 'britishImperial', 'grug'] : ['metric', 'usCustomary', 'britishImperial'];
  return (
    <Screen>
      <Section><ListItem title={t('ai.title')} icon="assistant" onPress={() => nav.navigate('Ai')} /></Section>
      <Section title={t('settings.data')}>
        <DataFileButtons download={t('settings.exportData')} upload={t('settings.importData')} />
      </Section>
      <Section title={t('settings.language')} icon="country">
        <Choice options={LANGUAGES} value={s.language} onChange={v => s.set({ language: v })} labels={v => (v === 'system' ? t('settings.system') : LANGUAGE_NAMES[v])} />
      </Section>
      <Section title={t('settings.unitSystem')} icon="unit">
        <Choice options={systems} value={s.unitSystem} onChange={v => s.set({ unitSystem: v })} labels={v => t(`settings.${v}`)} />
        <ToggleRow label={t('settings.convertUnits')} value={s.convertUnits} onChange={v => s.set({ convertUnits: v })} />
      </Section>
      <Section title={t('settings.theme')} icon="sparkles">
        <Row gap={spacing.m}>
          {THEMES.map(id => {
            const c = THEMES_DEF[id][THEMES_DEF[id].scheme ?? p.scheme];
            const on = s.theme === id;
            return (
              <Pressable
                key={id}
                accessibilityRole="radio"
                accessibilityState={{ selected: on }}
                accessibilityLabel={t(`settings.themes.${id}`)}
                onPress={() => s.set({ theme: id })}
                style={{ alignItems: 'center', gap: 4 }}>
                {/* A swatch: the theme's paper with its accent. */}
                <View style={{ width: 56, height: 40, borderRadius: radius.m, backgroundColor: c.background, borderWidth: on ? 2 : 1, borderColor: on ? p.tint : p.border, alignItems: 'center', justifyContent: 'center', flexDirection: 'row', gap: 4 }}>
                  <View style={{ width: 14, height: 14, borderRadius: 7, backgroundColor: c.tint }} />
                  <View style={{ width: 14, height: 14, borderRadius: 7, backgroundColor: c.success }} />
                </View>
                {labels ? <Caption>{t(`settings.themes.${id}`)}</Caption> : null}
              </Pressable>
            );
          })}
        </Row>
      </Section>
      <Section title={t('settings.accessibility')} icon="textSize">
        <ToggleRow label={t('settings.haptics')} value={s.hapticsEnabled} onChange={v => s.set({ hapticsEnabled: v })} />
        <ToggleRow label={t('settings.showLabels')} note={t('settings.showLabelsNote')} value={s.showLabels} onChange={v => s.set({ showLabels: v })} />
      </Section>
      <Section title={t('settings.calendar')} icon="planner">
        <Caption>{t('settings.calendarNote', { today: formatDay(localDay(), { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }, s.calendar) })}</Caption>
        <Choice options={CALENDARS} value={s.calendar} onChange={v => s.set({ calendar: v })} labels={v => t(`calendars.${v}`)} />
      </Section>
      <Section title={t('settings.holidays')} icon="holiday">
        <Caption>{t('settings.holidaysNote')}</Caption>
        <Row>
          {TRADITIONS.map(tr => {
            const on = s.traditions.includes(tr);
            return <Chip key={tr} label={t(`traditions.${tr}`)} selected={on} onPress={() => s.set({ traditions: on ? s.traditions.filter(x => x !== tr) : [...s.traditions, tr] })} />;
          })}
        </Row>
        <ToggleRow label={t('settings.holidayDecor')} note={t('settings.holidayDecorNote')} value={s.holidayDecor} onChange={v => s.set({ holidayDecor: v })} />
      </Section>
      {Platform.OS !== 'web' ? <Section><ListItem title={t('settings.openSystemSettings')} icon="settings" onPress={() => Linking.openSettings()} /></Section> : null}
    </Screen>
  );
}
